import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { computed, action, autorun } from 'mobx'
import { observer } from 'mobx-react'
import { withStyles } from 'material-ui/styles'
import { withRouter } from 'react-router-dom'
import inject from 'helpers/inject'

import ExpansionPanel, {
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  ExpansionPanelActions
} from 'material-ui/ExpansionPanel'
import Typography from 'material-ui/Typography'
import { InputLabel } from 'material-ui/Input'
import { MenuItem } from 'material-ui/Menu'
import { FormControl } from 'material-ui/Form'
import Select from 'material-ui/Select'
import { ChevronDown } from 'mdi-material-ui'
import NoKeys from 'components/NoKeys'
import TextField from 'material-ui/TextField'
import Button from 'material-ui/Button'
import EditKeyModal from 'components/EditKeyModal'
import AsyncButton from 'components/AsyncButton'

import RouterService from 'services/router'

import getServices from 'tasks/getServices'
import updateApiKey from 'tasks/updateApiKey'

import styles from 'styles/components/KeysList'

const KEY_LIST_ITEM_ID_PREFIX = 'key-list-item-'

@withStyles(styles)
@withRouter
@observer
export default class KeysList extends Component {

  static propTypes = {
    keys: PropTypes.arrayOf(PropTypes.shape({
      domains: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      service_type: PropTypes.number,
      blocked: PropTypes.bool.isRequired,
      created: PropTypes.string.isRequired
    })).isRequired
  }

  router = inject(RouterService)

  @computed
  get keys() {
    return this.props.keys.map(({ domains, ...rest }) => {
      domains = domains.replace(/,(?!\s)/g, ', ')
      return { domains, ...rest }
    })
  }

  @computed
  get subroute() {
    const { match, location } = this.props
    return location.pathname.replace(match.url, '').substr(1)
  }

  @computed
  get activeKeyId() {
    return +this.subroute.split('/')[0]
  }

  @computed
  get editKey() {
    if (this.subroute.split('/')[1] === 'edit')
      return this.keys.find(key => key.id === this.activeKeyId)
  }

  @computed
  get filter() {
    const filterStr = ( this.props.location.search || '' ).replace('?', '')
    const filterHash = {}
    filterStr.split('&').forEach(pair => {
      const [ key, value ] = pair.split('=')
      filterHash[key] = value
    })
    return filterHash
  }

  @computed
  get serviceFilter() {
    const { service } = this.filter
    return service ? +service : ''
  }

  @computed
  get domainsFilter() {
    return this.filter.domain || ''
  }

  @computed
  get filteredKeys() {
    let result = this.keys
    const service = this.serviceFilter
    const domain = this.domainsFilter
    if (service) result = result.filter(key => key.service_type === service)
    if (domain) result = result.filter(key => key.domains.indexOf(domain) > -1)
    return result
  }

  componentDidMount() {
    if (!getServices.result.length) getServices.perform()

    this.activeKeyTrackingDisposer = autorun(() => {
      const activeNode = document.getElementById(KEY_LIST_ITEM_ID_PREFIX + this.activeKeyId)
      if (activeNode)
        activeNode.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" })
    })
  }

  componentWillUnmount() {
    this.activeKeyTrackingDisposer()
  }

  render() {
    const { classes } = this.props
    const { activeKeyId, editKey } = this

    return (
      <div className={ classes.root }>
        <div className={ classes.filters }>
          <div className={ classes.filter }>
            <TextField
              fullWidth
              value={ this.domainsFilter }
              label="Поиск по домену"
              type="search"
              onChange={ this.handleSearchChange }
            />
          </div>
          <div className={ classes.filter }>
            <FormControl className={classes.formControl}>
              <InputLabel htmlFor="services-filter">Услуга</InputLabel>
              <Select
                fullWidth
                value={ this.serviceFilter }
                onChange={ this.handleSearchFilterChange }
                inputProps={{
                  name: 'services-filter',
                  id: 'services-filter',
                }}
              >
                <MenuItem value="">Сбросить фильтр</MenuItem>
                { getServices.result.map((service, index) => (
                  <MenuItem value={ service.id } key={ index } >{ service.name }</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        { this.filteredKeys.length ? this.filteredKeys.sort((key) => key.blocked ? 1 : -1).map((key, index) => (
          <ExpansionPanel
            key={ index }
            id={ KEY_LIST_ITEM_ID_PREFIX + key.id }
            expanded={ activeKeyId === key.id }
            onChange={ this.handleExpandedChange(key.id) }
          >
            <ExpansionPanelSummary expandIcon={<ChevronDown />}>
              <Typography color={ key.blocked && activeKeyId !== key.id ? 'secondary' : 'default' }>
                { activeKeyId === key.id &&
                  <span className={ classes.bold }>Домен: </span>
                }
                { key.domains }
              </Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <div className={ classes.container }>
                <Typography paragraph >
                  <span className={ classes.bold }>Услуга:</span> { key.service.name }
                </Typography>
                <Typography paragraph >
                  <span className={ classes.bold }>Статус:</span> { key.blocked ? 'Отключен' : 'Активен' }
                </Typography>
                <TextField
                  disabled
                  value={ key.key }
                  label="Ключ"
                  fullWidth
                />
              </div>
            </ExpansionPanelDetails>
            <ExpansionPanelActions>
              <AsyncButton
                action={ () => this.handleKeyRegenerate(key) }
                label='Перегенерировать'
                processingLabel=' Генерируем...'
                successLabel='Ключ обновлен!'
                ButtonProps={{
                  color: 'primary'
                }}
              />
              <AsyncButton
                action={ () => this.handleKeyBlockedToggle(key) }
                label={ key.blocked ? 'Активировать' : 'Отключить' }
                processingLabel={ key.blocked ? 'Активируем...' : 'Отключаем...' }
                successLabel={ key.blocked ? 'Ключ отключен!' : 'Ключ активирован!' }
                ButtonProps={{
                  color: 'primary'
                }}
              />
              <Button
                color='primary'
                onClick={ this.handleEditClick }
              >
                Редактировать
              </Button>
            </ExpansionPanelActions>
          </ExpansionPanel>
        )) : this.keys.length ? (
          <NoKeys message='Для выбранного фильтра нет ни одного ключа' />
        ) : (
          <NoKeys message='У Вас пока нет ни одного ключа' />
        )}
        <EditKeyModal apiKey={ editKey } />
      </div>
    )
  }

  @action.bound
  handleExpandedChange(keyId) {
    return (event, expanded) => {
      const { search } = this.props.location
      if (expanded)
        this.router.transitionTo(`/keys/${keyId}${search}`)
      else
        this.router.transitionTo(`/keys${search}`)
    }
  }

  @action.bound
  handleEditClick() {
    const { search } = this.props.location
    this.router.transitionTo(`/keys/${this.activeKeyId}/edit${search}`)
  }

  @action.bound
  async handleKeyBlockedToggle(key) {
    key.blocked = !key.blocked
    return await updateApiKey.perform(key)
  }

  @action.bound
  async handleKeyRegenerate(key) {
    key.key = ''
    return await updateApiKey.perform(key)
  }

  @action.bound
  handleSearchFilterChange(event) {
    this.applyFilter('service', event.target.value)
  }

  @action.bound
  handleSearchChange(event) {
    this.applyFilter('domain', event.target.value)
  }

  @action.bound
  applyFilter(param, newValue) {
    const { search, pathname } = this.props.location
    let searchStr = search.replace(/^\?/, '')
    if (searchStr.length > 0) {
      let matches = 0
      searchStr = searchStr.split('&').map(pairStr => {
        let [ key, value ] = pairStr.split('=')
        let result = `${key}=${value}`
        if (key === param) {
          matches++
          result = newValue ? `${key}=${newValue}` : null
        }
        return result
      }).filter(pair => !!pair).join('&')
      if (!matches && newValue)
        searchStr += `&${param}=${newValue}`
    } else {
      searchStr = `${param}=${newValue}`
    }
    const path = `${pathname}?${searchStr}`
    this.router.transitionTo(path)
  }

}



// WEBPACK FOOTER //
// ./src/components/KeysList.js